import React, { useState } from 'react';
import { Search, Plus, ExternalLink, CheckCircle, X, Save, Trash2 } from 'lucide-react';
import styles from './DataSourcesSettings.module.css';

// Import logo assets
import googleDriveLogo from '../../components/assets/google_drive_logo.svg';
import microsoftSharepointLogo from '../../components/assets/microsoft_sharepoint_logo.png';
import atlassianConfluenceLogo from '../../components/assets/atlassian_confluence_logo.svg';
import githubLogo from '../../components/assets/github_logo.svg';
import atlassianJiraLogo from '../../components/assets/atlassian_jira_logo.svg';
import mondayLogo from '../../components/assets/monday_logo.png';
import asanaLogo from '../../components/assets/asana_logo.svg';
import clickupLogo from '../../components/assets/clickup_logo.svg';
import jamaConnectLogo from '../../components/assets/jama_connect.png';
import buildkiteLogo from '../../components/assets/buildkite_logo.png';
import jenkinsLogo from '../../components/assets/jenkins_logo.png';
import circleciLogo from '../../components/assets/circleci_logo.png';
import siemensTeamcenterLogo from '../../components/assets/siemens_teamcenter_logo.png';
import ptcWindchillLogo from '../../components/assets/ptc_windchill_logo.png';
import dassaultEnoviaLogo from '../../components/assets/dassault_systemes_enovia_logo.png';
import sapPlmLogo from '../../components/assets/sap_plm_logo.png';
import oraclePlmLogo from '../../components/assets/oracle_fusion_cloud_plm.png';
import autodeskVaultLogo from '../../components/assets/autodesk_vault_logo.png';
import altium365Logo from '../../components/assets/altium365_logo.svg';
import gitlabLogo from '../../components/assets/gitlab_logo.svg';
import bitbucketLogo from '../../components/assets/atlassian_bitbucket_logo.svg';

export default function DataSourcesSettings() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showConnectPanel, setShowConnectPanel] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [connectionFormData, setConnectionFormData] = useState({
    serviceAccount: '',
    serviceAccountSecret: '',
    enabled: true
  });

  // Mock data for connected data sources
  const connectedSources = [
    {
      id: '1',
      name: 'Google Drive',
      logo: googleDriveLogo,
      category: 'Documents',
      status: 'connected',
      lastSync: '2 hours ago'
    },
    {
      id: '2',
      name: 'GitHub',
      logo: githubLogo,
      category: 'Code',
      status: 'connected',
      lastSync: '1 hour ago'
    },
    {
      id: '3',
      name: 'Microsoft SharePoint',
      logo: microsoftSharepointLogo,
      category: 'Documents',
      status: 'connected',
      lastSync: '30 minutes ago'
    }
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Available data sources to connect
  const availableSources = [
    // Documents
    { 
      name: 'Google Drive', 
      logo: googleDriveLogo,
      category: 'Documents'
    },
    { 
      name: 'Microsoft SharePoint', 
      logo: microsoftSharepointLogo,
      category: 'Documents'
    },
    { 
      name: 'Atlassian Confluence', 
      logo: atlassianConfluenceLogo,
      category: 'Documents'
    },
    
    // Code
    { 
      name: 'GitHub', 
      logo: githubLogo,
      category: 'Code'
    },
    { 
      name: 'GitLab', 
      logo: gitlabLogo,
      category: 'Code'
    },
    { 
      name: 'Atlassian Bitbucket', 
      logo: bitbucketLogo,
      category: 'Code'
    },
    
    // Project Management
    { 
      name: 'Atlassian Jira', 
      logo: atlassianJiraLogo,
      category: 'Project Management'
    },
    { 
      name: 'Monday.com', 
      logo: mondayLogo,
      category: 'Project Management'
    },
    { 
      name: 'Asana', 
      logo: asanaLogo,
      category: 'Project Management'
    },
    { 
      name: 'ClickUp', 
      logo: clickupLogo,
      category: 'Project Management'
    },
    
    // Requirements
    { 
      name: 'Jama Connect', 
      logo: jamaConnectLogo,
      category: 'Requirements'
    },
    
    // CI/CD
    { 
      name: 'BuildKite', 
      logo: buildkiteLogo,
      category: 'CI/CD'
    },
    { 
      name: 'Jenkins', 
      logo: jenkinsLogo,
      category: 'CI/CD'
    },
    { 
      name: 'CircleCI', 
      logo: circleciLogo,
      category: 'CI/CD'
    },
    
    // PLM/PDM
    { 
      name: 'Siemens Teamcenter', 
      logo: siemensTeamcenterLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'PTC Windchill', 
      logo: ptcWindchillLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'Dassault Systèmes ENOVIA', 
      logo: dassaultEnoviaLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'SAP PLM', 
      logo: sapPlmLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'Oracle Fusion Cloud PLM', 
      logo: oraclePlmLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'Autodesk Vault', 
      logo: autodeskVaultLogo,
      category: 'PLM/PDM'
    },
    { 
      name: 'Altium 365', 
      logo: altium365Logo,
      category: 'PLM/PDM'
    }
  ];

  const filteredSources = availableSources.filter(source =>
    source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    source.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleConnectSource = (source) => {
    setSelectedSource(source);
    setConnectionFormData({
      serviceAccount: '',
      serviceAccountSecret: '',
      enabled: true
    });
    setShowConnectionForm(true);
  };

  const handleCloseConnectPanel = () => {
    setShowConnectPanel(false);
    setSelectedSource(null);
  };

  const handleConnectionFormChange = (field, value) => {
    setConnectionFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveConnection = () => {
    console.log('Saving connection for:', selectedSource.name, connectionFormData);
    // TODO: Implement actual connection logic
    setShowConnectionForm(false);
    setSelectedSource(null);
  };

  const handleCancelConnection = () => {
    setShowConnectionForm(false);
    setSelectedSource(null);
  };

  const handleDeleteConnection = () => {
    console.log('Deleting connection for:', selectedSource.name);
    // TODO: Implement actual deletion logic
    setShowConnectionForm(false);
    setSelectedSource(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Documents': 'hsl(var(--primary))',
      'Code': 'hsl(var(--success))',
      'Project Management': 'hsl(var(--warning))',
      'Requirements': 'hsl(var(--destructive))',
      'CI/CD': 'hsl(var(--muted-foreground))',
      'PLM/PDM': 'hsl(var(--secondary))'
    };
    return colors[category] || 'hsl(var(--muted-foreground))';
  };

  return (
    <div className={styles.dataSourcesSettings}>
      {/* Connected Data Sources Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Connected Data Sources</h2>
            <p>Manage your connected data sources and integrations</p>
          </div>
          <button 
            className={styles.sectionButton}
            onClick={() => setShowConnectPanel(true)}
          >
            <Plus size={16} />
            <span>Connect new source</span>
          </button>
        </div>

        {/* Connected Sources List */}
        <div className={styles.connectedSourcesList}>
          {connectedSources.map(source => (
            <div key={source.id} className={styles.connectedSourceItem}>
              <div className={styles.sourceInfo}>
                <div 
                   className={styles.sourceLogo}
                 >
                   <img 
                     src={source.logo} 
                     alt={source.name.split(' ').map(word => word[0]).join('')}
                     style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                   />
                 </div>
                <div className={styles.sourceDetails}>
                  <div className={styles.sourceName}>{source.name}</div>
                  <div className={styles.sourceCategory}>{source.category}</div>
                  <div className={styles.sourceStatus}>
                    <CheckCircle size={14} />
                    <span>Connected • Last synced {source.lastSync}</span>
                  </div>
                </div>
              </div>
              <div className={styles.sourceActions}>
                <button className={styles.actionButton}>
                  <ExternalLink size={16} />
                  <span>Manage</span>
                </button>
                <button className={styles.actionButton}>
                  <X size={16} />
                  <span>Disconnect</span>
                </button>
              </div>
            </div>
          ))}
          
          {connectedSources.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔗</div>
              <h3>No data sources connected</h3>
              <p>Connect your first data source to start integrating with external services.</p>
            </div>
          )}
        </div>
      </div>

      {/* Connect New Sources Panel */}
      {showConnectPanel && (
        <div className={styles.connectPanel}>
          <div className={styles.connectPanelHeader}>
            <h3>Connect New Data Source</h3>
            <button 
              className={styles.closeButton}
              onClick={handleCloseConnectPanel}
            >
              <X size={16} />
            </button>
          </div>

          {/* Search Bar */}
          <div className={styles.searchContainer}>
            <div className={styles.searchBar}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search data sources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {/* Data Sources Grid */}
          <div className={styles.sourcesGrid}>
            {/* Group sources by category and sort alphabetically */}
            {(() => {
              // Group sources by category
              const groupedSources = filteredSources.reduce((groups, source) => {
                const category = source.category;
                if (!groups[category]) {
                  groups[category] = [];
                }
                groups[category].push(source);
                return groups;
              }, {});

              // Sort categories alphabetically
              const sortedCategories = Object.keys(groupedSources).sort();

              return sortedCategories.map(category => (
                <div key={category} className={styles.categorySection}>
                  <h4 className={styles.categoryTitle}>{category.toUpperCase()}</h4>
                  <div className={styles.categoryGrid}>
                    {groupedSources[category]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((source, index) => (
                      <div 
                        key={`${category}-${index}`}
                        className={styles.sourceCard}
                        onClick={() => handleConnectSource(source)}
                      >
                        <div 
                           className={styles.sourceLogo}
                         >
                          <img 
                            src={source.logo} 
                            alt={source.name.split(' ').map(word => word[0]).join('')}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </div>
                        <div className={styles.sourceName}>{source.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>

          {filteredSources.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>🔍</div>
              <h3>No data sources found</h3>
              <p>Try adjusting your search terms.</p>
            </div>
          )}
        </div>
      )}

      {/* Connection Form Modal */}
      {showConnectionForm && selectedSource && (
        <div className={styles.connectionFormOverlay}>
          <div className={styles.connectionFormCard}>
            <div className={styles.formHeader}>
              <div className={styles.sourceInfo}>
                <h3>{selectedSource.name}</h3>
              </div>
              <div className={styles.enableToggle}>
                <span>Enable</span>
                <label className={styles.toggleSwitch}>
                  <input
                    type="checkbox"
                    checked={connectionFormData.enabled}
                    onChange={(e) => handleConnectionFormChange('enabled', e.target.checked)}
                  />
                  <span className={styles.toggleSlider}></span>
                </label>
              </div>
            </div>

            <div className={styles.formFields}>
              <div className={styles.formField}>
                <label>Service Account</label>
                <input
                  type="text"
                  placeholder="Enter service account"
                  value={connectionFormData.serviceAccount}
                  onChange={(e) => handleConnectionFormChange('serviceAccount', e.target.value)}
                />
              </div>
              <div className={styles.formField}>
                <label>Service Account Secret</label>
                <input
                  type="password"
                  placeholder="Enter service account secret"
                  value={connectionFormData.serviceAccountSecret}
                  onChange={(e) => handleConnectionFormChange('serviceAccountSecret', e.target.value)}
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                className={styles.deleteButton}
                onClick={handleDeleteConnection}
              >
                <Trash2 size={16} />
                Delete data source
              </button>
              <div className={styles.rightActions}>
                <button
                  className={styles.cancelButton}
                  onClick={handleCancelConnection}
                >
                  Cancel
                </button>
                <button
                  className={styles.saveButton}
                  onClick={handleSaveConnection}
                >
                  <Save size={16} />
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
